import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_LATEST_VERSIONS_FOR_CSN, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";

import { Badge } from "@aws-amplify/ui-react";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuidv4 } from "uuid";

import { callDashboardLLM, callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../../amplify/api-gateway/dashboard-llm";
import { callLLMDashboardStreamable } from "../../../../amplify/api/llm-chat";
import { getWSService } from "../../../../amplify/api/llm-web-socket";
import { callWrite, EXPLAIN_CHARTING, SCAN_MULTI, UPDATE_LAST_VIEWED } from "../../../../amplify/api-gateway/write";
import { useDashboardTrigger } from "../../../../context/DashboardTriggerContext";
import useSetPagePatientVisit from "../../../common/GlobalOptionsContext";
import { dashboardLLMCache } from "../cache";
import { AdditionalContextModal } from "../common/AdditionalContextModal";
import { ScanMultiModal } from "../common/ScanMultiModal";
import { SectionCharting } from "../notes/SectionCharting";

export const CHARTING_JOB_CONFIG = [
    {
        key: "charting",
        file: EXPLAIN_CHARTING,
    },
];

let socketConnection: any;
let sessionId: string;

export const ChartingDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    unreadFileVersionMap?: Record<string, PatientFileVersion>,
    setUnreadFileVersionMap?: (map: Record<string, PatientFileVersion>) => void,
}) => {
    const { triggerAction, setTriggerAction } = useDashboardTrigger();
    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});
    const [feedbacks, setFeedbacks] = useState<LLMVisitFeedback[]>([]);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);

    const patientVisit = props.patientVisit;
    useSetPagePatientVisit(patientVisit);

    //
    // Scan multi modal state
    //
    const [isScanMultiModalOpen, setIsScanMultiModalOpen] = useState(false);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    //
    // Section state
    //
    const [charting, setCharting] = useState<any>();
    const [chartingActive, setChartingActive] = useState<number>(0);
    const [isLoadingCharting, setIsLoadingCharting] = useState<boolean>(false);

    //
    // Regeneration state
    //
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateTarget, setRegenerateTarget] = useState<string | undefined>();
    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);
    const regenerateKeyRef = useRef<any>();

    const accumulatedTextRef = useRef("");
    const lastSeqRef = useRef(0);

    const sectionToSetter = useRef<any>({
        charting: setCharting,
    });

    const sectionToSetIsLoading: Record<string, (val: boolean) => void> = {
        charting: setIsLoadingCharting,
    };

    useEffect(() => {
        sectionToSetter.current = {
            charting: setCharting,
        };
    }, []);

    useEffect(() => {
        regenerateKeyRef.current = "charting";
    }, [regenerateTarget]);

    const handleChatUpdate = useCallback((response: any) => {
        if (
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            (response["role"] === "COMPLETED" || response["role"] === "COMPLETED_WITH_ERROR")
        ) {
            if (props.patientVisit) {
                callQuery({
                    operation: GET_LATEST_VERSIONS_FOR_CSN,
                    csn: props.patientVisit.csn,
                }).then((latestVersions: PatientFileVersion[]) => {
                    const fileTypeToVersion: any = {};
                    for (let latestVersion of latestVersions) {
                        fileTypeToVersion[latestVersion.file_type] = latestVersion;
                    }
                    setLatestFileVersions(fileTypeToVersion);
                });
                dashboardLLMCache.get(EXPLAIN_CHARTING, props.patientVisit, props.tenant).then((cachedCharting) => {
                    sectionToSetter.current.charting?.(cachedCharting);
                    sectionToSetIsLoading.charting(false);
                });
                callGetDashboardLLMStatus(props.patientVisit.csn).then((newStatuses: Record<string, LLMDashboardStatus>) => {
                    setStatuses(newStatuses);
                });
            }
        }
    }, [props.patientVisit, props.tenant]);

    const handleWebSocketConnected = useCallback(() => {
        setIsWebSocketConnecting(false);
    }, []);

    const connect = () => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(props.ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            sessionId = socketConnection.sessionId;
        }

        if (!socketConnection.hasListener(handleChatUpdate)) {
            socketConnection.addMessageListener(handleChatUpdate);
        }
    };

    const loadCharting = useCallback(async (patient: PatientVisit) => {
        setIsLoadingCharting(true);
        const cached = await dashboardLLMCache.get(EXPLAIN_CHARTING, patient, props.tenant);
        setCharting(cached);
        setIsLoadingCharting(false);
    }, [props.tenant]);

    useEffect(() => {
        if (!patientVisit) return;

        loadCharting(patientVisit);

        callGetDashboardLLMStatus(patientVisit.csn).then((newStatuses: Record<string, LLMDashboardStatus>) => {
            setStatuses(newStatuses);
        });

        callQuery({
            operation: LLM_VISIT_FEEDBACK_BY_CSN,
            csn: patientVisit.csn,
        }).then((incomingFeedbacks: LLMVisitFeedback[]) => {
            setFeedbacks(incomingFeedbacks);
        });

        callQuery({
            operation: GET_LATEST_VERSIONS_FOR_CSN,
            csn: patientVisit.csn,
        }).then((latestVersions: PatientFileVersion[]) => {
            const fileTypeToVersion: any = {};
            for (let latestVersion of latestVersions) {
                fileTypeToVersion[latestVersion.file_type] = latestVersion;
            }
            setLatestFileVersions(fileTypeToVersion);
        });
    }, [patientVisit, loadCharting]);

    useEffect(() => {
        if (!patientVisit) return;

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: patientVisit.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            props.setUnreadFileVersionMap?.(fileVersionMap);

            callWrite({
                operation: UPDATE_LAST_VIEWED,
                csn: patientVisit.csn,
                jobs: CHARTING_JOB_CONFIG.map(config => config.file),
            }).then(() => {
                console.log("Completed UPDATE_LAST_VIEWED");
            });
        });
    }, [patientVisit, props.setUnreadFileVersionMap]);

    const regenerate = useCallback(async (shouldSyncData: boolean, target?: string) => {
        if (!patientVisit) return;

        setIsRegenerating(true);
        connect();
        sectionToSetIsLoading.charting(true);

        if (shouldSyncData) {
            await callDashboardLLM({
                operation: SCAN_MULTI,
                mrn: patientVisit.mrn,
                csn: patientVisit.csn,
            });
        }

        await callLLMDashboardStreamable(sessionId, {
            operation: EXPLAIN_CHARTING,
            mrn: patientVisit.mrn,
            csn: patientVisit.csn,
            report_date_str: new Date().toISOString().split('.')[0] + 'Z',
        });

        setIsRegenerating(false);
    }, [patientVisit]);

    useEffect(() => {
        if (triggerAction && triggerAction.name === 'charting') {
            regenerate(true);
            setTriggerAction(null);
        }
    }, [triggerAction, regenerate, setTriggerAction]);

    const handleShowScanMultiModalForSection = useCallback((patientFileVersion?: PatientFileVersion) => {
        if (patientFileVersion) {
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patientFileVersion.csn,
                file_type: 'scan_multi',
            }).then((versions: PatientFileVersion[]) => {
                const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
                const selectedIndex = sortedVersions.findIndex(v => v.version_id === patientFileVersion.source_version_id);
                const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

                setSelectedScanMultiVersion({
                    origVersion: previousVersion ? previousVersion.source_version_id : undefined,
                    newVersion: patientFileVersion.source_version_id,
                });
            });
            setSelectedTriggeringEvents(patientFileVersion.source_changed_keys);
            setIsScanMultiModalOpen(true);
        }
    }, []);

    const handleFileVersionChange = useCallback((fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion,
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        });
        setLatestFileVersions(updatedFileVersions);
    }, [latestFileVersions]);

    const regenerateButton = useMemo(() => {
        if (!patientVisit) return null;

        if (isRegenerating) {
            return (
                <Badge>
                    <FontAwesomeIcon className="notes-metadata" icon={faRefresh} spin /> Regenerating...
                </Badge>
            );
        } else {
            return (
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => { setRegenerateTarget(undefined); setShowContextMenu(true); }}
                    disabled={isRegenerating}
                >
                    <FontAwesomeIcon icon={faRefresh} /> Regenerate
                </Button>
            );
        }
    }, [patientVisit, isRegenerating]);

    return (
        <Container className="notes-dashboard">
            <Row className="mb-3">
                <Col>
                    <span className="gradient-text" style={{ fontSize: '1.25rem' }}>Charting</span>
                </Col>
                <Col className="text-right">
                    {patientVisit && (
                        <>
                            {regenerateButton}
                            <AdditionalContextModal
                                regenerate={regenerate}
                                showModal={showContextMenu}
                                setShowModal={setShowContextMenu}
                                patientVisit={patientVisit}
                                target={regenerateTarget}
                                tenant={props.tenant}
                            />
                        </>
                    )}
                </Col>
            </Row>

            <SectionCharting
                className="dashboard-section mb-3"
                isLoading={isLoadingCharting}
                tenant={props.tenant}
                data={charting}
                active={chartingActive}
                setActive={setChartingActive}
                patientVisit={patientVisit}
                feedbacks={feedbacks}
                setFeedbacks={setFeedbacks}
                statuses={statuses}
                setRegenerateTarget={setRegenerateTarget}
                setShowContextMenu={setShowContextMenu}
                latestFileVersions={latestFileVersions}
                handleShowScanMultiModal={handleShowScanMultiModalForSection}
                sectionToSetter={sectionToSetter.current}
                handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={props.unreadFileVersionMap}
            />

            {patientVisit && (
                <ScanMultiModal
                    patient={patientVisit}
                    isOpen={isScanMultiModalOpen}
                    onClose={() => { setIsScanMultiModalOpen(false); }}
                    versionInfo={selectedScanMultiVersion}
                    triggeringEvents={selectedTriggeringEvents}
                />
            )}
        </Container>
    );
};
